import React, { useEffect, useMemo, useState } from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import './EnterKeyPager.css';
const PAGE_SIZES = [25, 50, 100];
function getMessage(messageKey, defaultMessage, messagesMap, localization) {
    const message = messagesMap ? messagesMap(messageKey) : { messageKey, defaultMessage };
    return localization.toLanguageString(message.messageKey, message.defaultMessage);
}
function formatInfo(template, firstItem, lastItem, total) {
    return template.replace('{0}', String(firstItem)).replace('{1}', String(lastItem)).replace('{2}', String(total));
}
export default function EnterKeyPager({ total, skip, take, onPageChange, pageSizes = PAGE_SIZES, pageSizeValue, messagesMap, className = '' }) {
    const localization = useLocalization();
    const totalPages = Math.max(1, Math.ceil(total / take));
    const currentPage = Math.floor(skip / take) + 1;
    const [draftPage, setDraftPage] = useState(String(currentPage));
    const handlePageChange = onPageChange;
    useEffect(() => {
        setDraftPage(String(currentPage));
    }, [currentPage]);
    const pageSizeOptions = useMemo(() => {
        const configuredPageSizes = pageSizes.length > 0 ? pageSizes : PAGE_SIZES;
        return configuredPageSizes.includes(take) ? configuredPageSizes : [take, ...configuredPageSizes];
    }, [pageSizes, take]);
    const emitPageChange = (nextPage, event) => {
        if (!handlePageChange || nextPage < 1 || nextPage > totalPages || nextPage === currentPage)
            return;
        handlePageChange({
            skip: (nextPage - 1) * take,
            take,
            syntheticEvent: event,
            nativeEvent: event.nativeEvent
        });
    };
    const handleInputChange = (event) => {
        setDraftPage(event.target.value);
    };
    const handleInputKeyDown = (event) => {
        if (event.key !== 'Enter')
            return;
        const nextPage = Number(draftPage);
        if (!Number.isInteger(nextPage)) {
            setDraftPage(String(currentPage));
            return;
        }
        const clampedPage = Math.min(Math.max(nextPage, 1), totalPages);
        setDraftPage(String(clampedPage));
        emitPageChange(clampedPage, event);
    };
    const handlePageSizeChange = (event) => {
        const nextTake = Number(event.target.value);
        if (!handlePageChange || !Number.isInteger(nextTake))
            return;
        handlePageChange({
            skip: 0,
            take: nextTake,
            syntheticEvent: event,
            nativeEvent: event.nativeEvent
        });
    };
    const firstItem = total === 0 ? 0 : Math.min(skip + 1, total);
    const lastItem = Math.min(skip + take, total);
    const selectedPageSize = pageSizeValue !== null && pageSizeValue !== void 0 ? pageSizeValue : take;
    const rootClass = ['k-pager', 'k-grid-pager', 'k-pager-md', 'ip-enter-key-pager', className]
        .filter(Boolean)
        .join(' ');
    return (React.createElement("div", { className: rootClass, role: "application", "aria-roledescription": "pager" },
        React.createElement("div", { className: "k-pager-numbers-wrap" },
            React.createElement("button", { type: "button", className: "k-button k-button-md k-rounded-md k-button-flat k-button-flat-base k-pager-nav k-pager-first", "aria-disabled": currentPage === 1, disabled: currentPage === 1, title: getMessage('pager.firstPage', 'Go to the first page', messagesMap, localization), onClick: (event) => emitPageChange(1, event) }, "\u00AB"),
            React.createElement("button", { type: "button", className: "k-button k-button-md k-rounded-md k-button-flat k-button-flat-base k-pager-nav", "aria-disabled": currentPage === 1, disabled: currentPage === 1, title: getMessage('pager.previousPage', 'Go to the previous page', messagesMap, localization), onClick: (event) => emitPageChange(currentPage - 1, event) }, "\u2039"),
            React.createElement("span", { className: "k-pager-input" },
                React.createElement("span", null, getMessage('pager.page', 'Page', messagesMap, localization)),
                React.createElement("input", { "aria-label": getMessage('pager.page', 'Page', messagesMap, localization), className: "k-input-inner", type: "number", min: 1, max: totalPages, value: draftPage, onChange: handleInputChange, onKeyDown: handleInputKeyDown }),
                React.createElement("span", null,
                    getMessage('pager.of', 'of', messagesMap, localization),
                    " ",
                    totalPages)),
            React.createElement("button", { type: "button", className: "k-button k-button-md k-rounded-md k-button-flat k-button-flat-base k-pager-nav", "aria-disabled": currentPage >= totalPages, disabled: currentPage >= totalPages, title: getMessage('pager.nextPage', 'Go to the next page', messagesMap, localization), onClick: (event) => emitPageChange(currentPage + 1, event) }, "\u203A"),
            React.createElement("button", { type: "button", className: "k-button k-button-md k-rounded-md k-button-flat k-button-flat-base k-pager-nav k-pager-last", "aria-disabled": currentPage >= totalPages, disabled: currentPage >= totalPages, title: getMessage('pager.lastPage', 'Go to the last page', messagesMap, localization), onClick: (event) => emitPageChange(totalPages, event) }, "\u00BB")),
        pageSizeOptions.length > 0 && (React.createElement("span", { className: "k-pager-sizes" },
            React.createElement("select", { "aria-label": getMessage('pager.pageSizeAriaLabel', 'Page size', messagesMap, localization), value: selectedPageSize, onChange: handlePageSizeChange }, pageSizeOptions.map((pageSize) => (React.createElement("option", { key: pageSize, value: pageSize }, pageSize)))),
            React.createElement("span", null, getMessage('pager.itemsPerPage', 'items per page', messagesMap, localization)))),
        React.createElement("span", { className: "k-pager-info" }, formatInfo(getMessage('pager.info', '{0} - {1} of {2} items', messagesMap, localization), firstItem, lastItem, total))));
}
